import "moment/locale/he";
import moment from "moment/min/moment-with-locales";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ImageWithFallback from "../../../shared/imageWithFallback";
import ScreenHeader from "../../components/ScreenHeader";
import "./orderConfirmation.scss";

const CheckIcon = () => (
  <div className="oc-v2-check">
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M4 12.5L9.5 18L20 6.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </div>
);

const CalendarIcon = () => (
  <svg className="oc-v2-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" />
    <path d="M3 9H21" stroke="currentColor" strokeWidth="1.8" />
    <path d="M8 3V6M16 3V6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const PinIcon = () => (
  <svg className="oc-v2-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 21C12 21 19 15.5 19 10A7 7 0 1 0 5 10C5 15.5 12 21 12 21Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    <circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

const DetailsIcon = () => (
  <svg className="oc-v2-section-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="3" width="16" height="18" rx="2" stroke="currentColor" strokeWidth="1.8" />
    <path d="M8 8H16M8 12H16M8 16H13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const CancelIcon = () => (
  <svg className="oc-v2-section-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5 5L19 19M19 5L5 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// V2 order confirmation (/orderConfirmation) — presentational. All data arrives via props
// from the V1 screens/orderConfirmation.jsx gate.
function OrderConfirmation({
  type,
  clubName,
  lessonName,
  lessonEndTime,
  address,
  date,
  hours,
  minutes,
  nowDate,
  cancellationTime,
  userName,
  orderNumber,
  isOrthodox,
  imgSrc,
  defaultImg,
  imgFallbackSrc,
  onDone,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isCheckin = type === "checkin";

  // Mirrors V1's two display branches: nowDate carries separate hours/minutes,
  // otherwise `date` is the lesson's full start "DD.MM.YYYY HH:mm".
  // Check-in shows no date row per design (entry is immediate).
  const dateLine = (() => {
    try {
      if (isCheckin || !date) return null;
      if (nowDate) {
        const m = moment(date, ["DD-MM-YYYY", "DD/MM/YYYY", "DD.MM.YYYY"]).locale("he");
        return `${m.format("dddd, D MMMM")} ${hours}:${minutes}`;
      }
      const m = moment(date, "DD.MM.YYYY HH:mm").locale("he");
      return `${m.format("dddd, D MMMM")} ${m.format("HH:mm")}${lessonEndTime ? ` - ${lessonEndTime}` : ""}`;
    } catch {
      return null;
    }
  })();

  return (
    <div className="oc-v2">
      <ScreenHeader title={t("ORDER_CONFIRMATION")} onBack={() => navigate(-1)} />

      <div className="oc-v2-hero">
        <ImageWithFallback src={isOrthodox ? defaultImg : imgSrc} fallbackSrc={imgFallbackSrc} alt="" />
      </div>

      <div className="oc-v2-sheet">
        <CheckIcon />
        <p className="oc-v2-title">{isCheckin ? t("CHECKIN_CONFIRMED_TITLE") : t("CLASS_BOOKED")}</p>
        <div className="oc-v2-divider" />
        <div className="oc-v2-order-card">
          <div className="oc-v2-order-img">
            <ImageWithFallback src={isOrthodox ? defaultImg : imgSrc} fallbackSrc={imgFallbackSrc} alt="Club" />
          </div>
          <div className="oc-v2-order-info">
            <p className="oc-v2-club">{clubName}</p>
            {lessonName && <p className="oc-v2-activity">{lessonName}</p>}
            {dateLine && (
              <p className="oc-v2-meta">
                <CalendarIcon />
                {dateLine}
              </p>
            )}
            {address && (
              <p className="oc-v2-meta">
                <PinIcon />
                {address}
              </p>
            )}
          </div>
        </div>

        <div className="oc-v2-divider" />

        <div className="oc-v2-section">
          <DetailsIcon />
          <div className="oc-v2-section-texts">
            <p className="oc-v2-section-title">{t("ORDER_DETAILS_TITLE")}</p>
            {userName && (
              <p className="oc-v2-section-line">
                <span className="oc-v2-section-label">{t("ORDER_DETAILS_NAME")}: </span>
                {userName}
              </p>
            )}
            {orderNumber && (
              <p className="oc-v2-section-line">
                <span className="oc-v2-section-label">{t("ORDER_DETAILS_NUMBER")}: </span>
                <span dir="ltr">#{orderNumber}</span>
              </p>
            )}
          </div>
        </div>

        <div className="oc-v2-section">
          <CancelIcon />
          <div className="oc-v2-section-texts">
            <p className="oc-v2-section-title">{t("CANCEL_TIME")}</p>
            <p className="oc-v2-section-line">
              {isCheckin
                ? t("CHECKIN_NO_CANCEL")
                : cancellationTime > 0
                  ? t("CANCEL_TIME_LEFT", { hours: cancellationTime })
                  : t("CANCELLATION_TIME_NO_LIMIT")}
            </p>
          </div>
        </div>
      </div>

      <div className="oc-v2-bottom-bar">
        <button type="button" className="oc-v2-cta" onClick={onDone}>
          {t("CONFIRM_GOT_IT")}
        </button>
      </div>
    </div>
  );
}

export default OrderConfirmation;
